import { ActionFunction, redirect } from 'react-router-dom';
import debug from 'debug';

// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client, cacheApolloPersistor, pruneCacheRetention } from '../../../gql/client';
import {
  hydrateSessionFromSeed,
  startSeedWriter,
} from '../../../gql/cacheSeed';
import { SIGN_IN } from '../../../gql/mutations/auth';
import { GET_APP_CONFIG } from '../../../gql/queries/appConfig';
import i18n from '../../../i18n';

// Utils
import { setSessionStorageUser } from '../../../utils/sessionStorage';
import { offerToSaveCredentials } from '../../../utils/savedCredentials';

const log = debug('rois:pages:auth:login');

if (import.meta.env.DEV) {
  debug.enable('rois:pages:auth:login*');
}

// Type
import { AuthContextType } from '../../../hooks/useAuth';

export const ActionLogin: (
  userData: AuthContextType | null
) => ActionFunction | undefined = (userData) => async (formDataRaw) => {
  const formData = Object.fromEntries(await formDataRaw.request.formData());

  try {
    const {
      data: { signIn },
    } = await client.mutate({
      mutation: SIGN_IN,
      variables: {
        input: {
          email: formData.email,
          password: formData.password,
        },
      },
    });

    const authPayload = {
      user: {
        id: signIn.user.id,
        email: signIn.user.email,
        name: signIn.user.name,
        surName: signIn.user.surName,
        userType: signIn.user.userType,
        rolActive: signIn.user.rolActive,
        roles: signIn.user.roles,
        permissions: signIn.user.permissions,
        accessZones: signIn.user.accessZones,
        validated: signIn.user.validated,
        // Team membership (M2M support)
        teamIDs: signIn.user.teamIDs || [], // ✅ M2M: Store all team IDs
        // Multi-tenant fields
        activeTenantId: signIn.user.activeTenantId,
      },
      accessToken: signIn.accessToken,
      refreshToken: signIn.refreshToken,
      // Multi-tenant: availableTenants a nivel de Auth (no dentro de user)
      availableTenants: signIn.availableTenants || [],
    };

    Sentry.setUser({
      id: signIn.user.id,
      email: signIn.user.email,
      username: `${signIn.user.name} ${signIn.user.surName}`,
      rolActive: signIn.user.rolActive,
    });

    if (!signIn.user.validated) {
      // Write to sessionStorage directly WITHOUT triggering React state update.
      // Calling setUser() here would trigger a re-render of the Router component,
      // which recreates createBrowserRouter(), destroying the redirect in progress.
      setSessionStorageUser(authPayload);
      return redirect('/reset-password');
    }

    // For validated users: update React state normally
    userData?.setUser(authPayload);

    // Ofrecer al gestor del sistema que guarde la contraseña (solo app nativa).
    //
    // Va AQUÍ y no en el formulario porque este es el único punto que sabe que
    // el backend aceptó la credencial: guardar lo que se teclea, sin esperar la
    // respuesta, llenaría el gestor de contraseñas equivocadas.
    //
    // Queda fuera del camino del usuario con no validado a propósito: ese va a
    // cambiar la contraseña a continuación, así que guardarla sería guardar una
    // que está a punto de dejar de servir.
    //
    // Sin `await`: el diálogo lo pinta el sistema por encima de la actividad y
    // no debe retrasar la entrada.
    offerToSaveCredentials(String(formData.email), String(formData.password));

    // Arranque CALIENTE en pestaña nueva.
    //
    // Este es el momento en que por primera vez se sabe QUIÉN entra: hasta
    // ahora no había identidad y por eso no se podía localizar la semilla (la
    // clave es `tenantId::userId`). Si esta pestaña arrancó sin snapshot —el
    // caso de una pestaña nueva, donde `sessionStorage` está vacío— y hay
    // semilla guardada de una sesión anterior de ESTE usuario, se restaura
    // aquí. Sin esto, la app paga el waterfall entero: 10-15 s en blanco.
    //
    // Va ANTES de la query de appConfig a propósito: `restore()` reemplaza la
    // caché en memoria, así que hacerlo después pisaría lo que ya se hubiera
    // traído de la red.
    try {
      if (await hydrateSessionFromSeed()) {
        await cacheApolloPersistor.restore();
        // La semilla puede llevar días: lo de más de 7 sin red no se sirve.
        pruneCacheRetention('login:prune');
      }
      startSeedWriter();
    } catch (seedError) {
      // Una semilla ilegible nunca puede impedir entrar: se sigue en frío,
      // que es exactamente el comportamiento anterior.
      log('⚠️ No se pudo sembrar la caché tras el login:', seedError);
    }

    // 🔧 CARGAR APP CONFIG INMEDIATAMENTE DESPUÉS DEL LOGIN
    // Esto asegura que availableConcessionIds esté disponible ANTES de que se carguen las rutas
    try {
      const {
        data: { appConfig },
      } = await client.query({
        query: GET_APP_CONFIG,
        fetchPolicy: 'network-only', // Siempre obtener datos frescos
      });

      if (appConfig) {
        // Guardar en sessionStorage para que esté disponible inmediatamente
        sessionStorage.setItem('appConfig', JSON.stringify(appConfig));
        log('✅ AppConfig pre-cargado:', appConfig);
      }
    } catch (appConfigError) {
      // No bloquear el login si falla la carga de appConfig
      console.warn('⚠️ [LOGIN] Error cargando appConfig:', appConfigError);
      Sentry.captureException(appConfigError);
    }
  } catch (err) {
    Sentry.captureException(err);
    const errorMessage = (err as Error)?.message;
    console.log('[auth] error de inicio de sesión', err);
    return errorMessage;
  }

  return null;
};
